import { assertContentWriteReadback } from '@modules/content-plan/services/contentConsumerTransport';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
/**
 * ContentStrategyFirestoreService — Firestore CRUD for ContentStrategy
 * + nested sprints, research packs, and posts.
 *
 * Mirrors InsightsFirestoreService pattern. Collection paths:
 *   projects/{pid}/content_strategies/{sid}
 *   projects/{pid}/content_strategies/{sid}/sprints/{sprintId}
 *   projects/{pid}/content_strategies/{sid}/sprints/{sprintId}/posts/{postId}
 *   projects/{pid}/content_strategies/{sid}/research_packs/{rpId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  type DocumentReference,
  type Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type {
  ContentStrategy,
  ContentSprint,
  ContentPost,
  ResearchPack,
  StrategyPlatformCadence,
} from '../types';
import { stripUndefined } from '../utils/stripUndefined';

function createdAtMillis(strategy: ContentStrategy): number {
  const value = (strategy as { createdAt?: unknown }).createdAt;
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value instanceof Date) return value.getTime();
  return 0;
}

function sortNewestFirst(strategies: ContentStrategy[]): ContentStrategy[] {
  return [...strategies].sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
}

export class ContentStrategyFirestoreService {
  private readonly db = CoreFirebaseService.getInstance().firestore;

  // ─── Strategy ────────────────────────────────────────────────────

  strategyPath(projectId: string, strategyId: string) {
    return doc(this.db, 'projects', projectId, 'content_strategies', strategyId);
  }

  strategiesCollection(projectId: string) {
    return collection(this.db, 'projects', projectId, 'content_strategies');
  }

  async getStrategy(projectId: string, strategyId: string): Promise<ContentStrategy | null> {
    const snap = await getDoc(this.strategyPath(projectId, strategyId));
    return snap.exists() ? (snap.data() as ContentStrategy) : null;
  }

  async listStrategies(projectId: string, authorId?: string | null): Promise<ContentStrategy[]> {
    const q = authorId && authorId !== 'all'
      ? query(this.strategiesCollection(projectId), where('authorId', '==', authorId))
      : query(this.strategiesCollection(projectId), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    const strategies = snap.docs.map((d) => d.data() as ContentStrategy);
    return authorId && authorId !== 'all' ? sortNewestFirst(strategies) : strategies;
  }

  /** Realtime subscription for the strategy list (drives SubTab 1 Strategies). */
  subscribeStrategies(
    projectId: string,
    authorId: string | null | undefined,
    callback: (strategies: ContentStrategy[]) => void,
  ): Unsubscribe {
    const q = authorId && authorId !== 'all'
      ? query(this.strategiesCollection(projectId), where('authorId', '==', authorId))
      : query(this.strategiesCollection(projectId), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      const strategies = snap.docs.map((d) => d.data() as ContentStrategy);
      callback(authorId && authorId !== 'all' ? sortNewestFirst(strategies) : strategies);
    });
  }

  /** Realtime subscription for a single strategy doc. */
  subscribeStrategy(
    projectId: string,
    strategyId: string,
    callback: (strategy: ContentStrategy | null) => void,
  ): Unsubscribe {
    return onSnapshot(this.strategyPath(projectId, strategyId), (snap) => {
      callback(snap.exists() ? (snap.data() as ContentStrategy) : null);
    });
  }

  async saveStrategy(projectId: string, strategy: ContentStrategy): Promise<void> {
    await new ContentPlanApiClient().saveStrategyRecord(projectId, stripUndefined(strategy));
    assertContentWriteReadback((await getDoc(this.strategyPath(projectId, strategy.id))).data(), stripUndefined(strategy), true);
  }

  async deleteStrategy(projectId: string, strategyId: string): Promise<void> {
    await deleteDoc(this.strategyPath(projectId, strategyId));
  }

  async deleteStrategyWithArtifacts(
    projectId: string,
    strategyId: string,
  ): Promise<{
    strategies: number;
    sprints: number;
    sprintPosts: number;
    researchPacks: number;
    publications: number;
    contentPosts: number;
    postGenerationRuns: number;
    postGenerationDrafts: number;
    topicBacklinksUpdated: number;
  }> {
    const strategyRef = this.strategyPath(projectId, strategyId);
    const refsToDelete: DocumentReference[] = [];
    const topicSprintRefs = new Map<string, Set<string>>();
    let sprintPosts = 0;

    const sprintsSnap = await getDocs(this.sprintsCollection(projectId, strategyId));
    for (const sprintDoc of sprintsSnap.docs) {
      const sprint = sprintDoc.data() as ContentSprint & {
        assignedTopicIds?: string[];
        assignments?: Array<{ topicId?: string }>;
      };
      const sprintId = String(sprint.id ?? sprintDoc.id);

      const topicIds = new Set<string>();
      for (const topicId of sprint.assignedTopicIds ?? []) {
        if (topicId) topicIds.add(String(topicId));
      }
      for (const assignment of sprint.assignments ?? []) {
        if (assignment?.topicId) topicIds.add(String(assignment.topicId));
      }
      for (const topicId of topicIds) {
        const sprintIds = topicSprintRefs.get(topicId) ?? new Set<string>();
        sprintIds.add(sprintId);
        topicSprintRefs.set(topicId, sprintIds);
      }

      const postsSnap = await getDocs(this.postsCollection(projectId, strategyId, sprintDoc.id));
      sprintPosts += postsSnap.docs.length;
      refsToDelete.push(...postsSnap.docs.map((d) => d.ref));
      refsToDelete.push(sprintDoc.ref);
    }

    const researchPacksSnap = await getDocs(this.researchPacksCollection(projectId, strategyId));
    refsToDelete.push(...researchPacksSnap.docs.map((d) => d.ref));

    const publicationsSnap = await getDocs(collection(strategyRef, 'publications'));
    refsToDelete.push(...publicationsSnap.docs.map((d) => d.ref));

    const contentPostsSnap = await getDocs(query(
      collection(this.db, 'projects', projectId, 'content_posts'),
      where('strategyId', '==', strategyId),
    ));
    refsToDelete.push(...contentPostsSnap.docs.map((d) => d.ref));

    let postGenerationDrafts = 0;
    const postGenerationsSnap = await getDocs(query(
      collection(this.db, 'projects', projectId, 'post_generations'),
      where('strategyId', '==', strategyId),
    ));
    for (const runDoc of postGenerationsSnap.docs) {
      const draftsSnap = await getDocs(collection(runDoc.ref, 'generatedPosts'));
      postGenerationDrafts += draftsSnap.docs.length;
      refsToDelete.push(...draftsSnap.docs.map((d) => d.ref));
      refsToDelete.push(runDoc.ref);
    }

    refsToDelete.push(strategyRef);

    const topicBacklinksUpdated = await this.removeStrategyTopicBacklinks(projectId, topicSprintRefs);
    await this.deleteRefsInChunks(refsToDelete);

    return {
      strategies: 1,
      sprints: sprintsSnap.docs.length,
      sprintPosts,
      researchPacks: researchPacksSnap.docs.length,
      publications: publicationsSnap.docs.length,
      contentPosts: contentPostsSnap.docs.length,
      postGenerationRuns: postGenerationsSnap.docs.length,
      postGenerationDrafts,
      topicBacklinksUpdated,
    };
  }

  private async removeStrategyTopicBacklinks(
    projectId: string,
    topicSprintRefs: Map<string, Set<string>>,
  ): Promise<number> {
    const topicIds = Array.from(topicSprintRefs.keys());
    let updated = 0;
    const CHUNK = 400;
    for (let i = 0; i < topicIds.length; i += CHUNK) {
      const batch = writeBatch(this.db);
      let ops = 0;
      for (const topicId of topicIds.slice(i, i + CHUNK)) {
        const topicRef = doc(this.db, 'projects', projectId, 'topic_backlog', topicId);
        const topicSnap = await getDoc(topicRef);
        if (!topicSnap.exists()) continue;
        const data = topicSnap.data() as { usedInSprintIds?: string[]; status?: string };
        const removedSprintIds = topicSprintRefs.get(topicId) ?? new Set<string>();
        const remainingSprintIds = (data.usedInSprintIds ?? [])
          .map((id) => String(id))
          .filter((id) => !removedSprintIds.has(id));
        const patch: Record<string, unknown> = {
          usedInSprintIds: Array.from(new Set(remainingSprintIds)),
          updatedAt: serverTimestamp(),
        };
        if (data.status === 'assigned' && remainingSprintIds.length === 0) {
          patch.status = 'ready';
        }
        batch.update(topicRef, patch);
        ops += 1;
      }
      if (ops > 0) {
        await batch.commit();
        updated += ops;
      }
    }
    return updated;
  }

  private async deleteRefsInChunks(refs: DocumentReference[]): Promise<void> {
    const CHUNK = 400;
    for (let i = 0; i < refs.length; i += CHUNK) {
      const batch = writeBatch(this.db);
      for (const ref of refs.slice(i, i + CHUNK)) {
        batch.delete(ref);
      }
      await batch.commit();
    }
  }

  // ─── Calendar fields (Sprint — Calendar tab) ─────────────────────

  /**
   * Replace the strategy's per-platform cadence list. Uses updateDoc with
   * the single top-level field so no other nested keys leak (per project
   * memory feedback_firestore_merge_bug).
   */
  async updatePlatformCadences(
    projectId: string,
    strategyId: string,
    cadences: StrategyPlatformCadence[],
  ): Promise<void> {
    await updateDoc(this.strategyPath(projectId, strategyId), {
      platformCadences: stripUndefined(cadences),
      updatedAt: serverTimestamp(),
    });
  }

  /** Persist the legend color once assigned by the client-side palette. */
  async updateDisplayColor(
    projectId: string,
    strategyId: string,
    displayColor: string,
  ): Promise<void> {
    await updateDoc(this.strategyPath(projectId, strategyId), {
      displayColor,
      updatedAt: serverTimestamp(),
    });
  }

  // ─── Sprints ────────────────────────────────────────────────────

  sprintsCollection(projectId: string, strategyId: string) {
    return collection(this.db, 'projects', projectId, 'content_strategies', strategyId, 'sprints');
  }

  async listSprints(projectId: string, strategyId: string): Promise<ContentSprint[]> {
    const snap = await getDocs(query(this.sprintsCollection(projectId, strategyId), orderBy('dateStart', 'asc')));
    return snap.docs.map((d) => d.data() as ContentSprint);
  }

  subscribeSprints(
    projectId: string,
    strategyId: string,
    callback: (sprints: ContentSprint[]) => void,
  ): Unsubscribe {
    const q = query(this.sprintsCollection(projectId, strategyId), orderBy('dateStart', 'asc'));
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => d.data() as ContentSprint));
    });
  }

  // ─── Posts (under a sprint) ────────────────────────────────────

  postsCollection(projectId: string, strategyId: string, sprintId: string) {
    return collection(
      this.db,
      'projects', projectId,
      'content_strategies', strategyId,
      'sprints', sprintId,
      'posts',
    );
  }

  async listPostsForSprint(
    projectId: string,
    strategyId: string,
    sprintId: string,
  ): Promise<ContentPost[]> {
    const snap = await getDocs(this.postsCollection(projectId, strategyId, sprintId));
    return snap.docs.map((d) => d.data() as ContentPost);
  }

  subscribePostsForSprint(
    projectId: string,
    strategyId: string,
    sprintId: string,
    callback: (posts: ContentPost[]) => void,
  ): Unsubscribe {
    return onSnapshot(this.postsCollection(projectId, strategyId, sprintId), (snap) => {
      callback(snap.docs.map((d) => d.data() as ContentPost));
    });
  }

  async savePost(
    projectId: string,
    strategyId: string,
    sprintId: string,
    post: ContentPost,
  ): Promise<void> {
    await setDoc(doc(this.postsCollection(projectId, strategyId, sprintId), post.id), post, { merge: true });
  }

  // ─── Research packs ─────────────────────────────────────────────

  researchPacksCollection(projectId: string, strategyId: string) {
    return collection(
      this.db,
      'projects', projectId,
      'content_strategies', strategyId,
      'research_packs',
    );
  }

  async listResearchPacks(projectId: string, strategyId: string): Promise<ResearchPack[]> {
    const snap = await getDocs(this.researchPacksCollection(projectId, strategyId));
    return snap.docs.map((d) => d.data() as ResearchPack);
  }
}
